import { useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { request } from '~/services/common';

import type { Lens, LensResponse } from '~/types/leLens';

import { encodeRequestBody } from '~/utils/encodeRequestBody';
import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { reportingLeLensQueryKeys } from './useGetReportingLeLens';
import { refreshCertifiedShelves } from './useLensCertification';

export type LensRevision = {
  version: number;
  actorEmail: string;
  serviceEmail?: string;
  reason: string;
  createdAt: string;
};
const path = (id: string, suffix: string) => resolveReportingLeLensUrl(`/api/reporting/le-lenses/${id}/${suffix}`);
const keys = (id: string, resource: string) => ['lens-collaboration', id, resource];

export function useLensRevisions(id: string, enabled: boolean, offset = 0) {
  return useQuery({
    queryKey: [...keys(id, 'revisions'), offset],
    enabled,
    queryFn: async () =>
      (await request(path(id, `revisions?offset=${offset}`))) as {
        revisions: Array<LensRevision>;
        currentVersion: number;
        nextOffset: number | null;
      },
    retry: false,
  });
}

export function useLensRevision(id: string, version: number | null) {
  return useQuery({
    queryKey: keys(id, `revision:${version}`),
    enabled: version !== null,
    queryFn: async () => (await request(path(id, `revisions/${version}`))) as { lens: Lens },
    retry: false,
  });
}

export function useLensCollaborationMutation(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async ({ suffix, body }: { suffix: string; body: unknown }) =>
      (await request(path(id, suffix), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(encodeRequestBody(body)),
      })) as { lens?: Lens },
    onSuccess: async ({ lens }) => {
      if (lens) {
        client.setQueryData<LensResponse>(reportingLeLensQueryKeys.detail(id), (current) =>
          current ? { ...current, lens } : current,
        );
      }
      await client.invalidateQueries({ queryKey: ['lens-collaboration', id] });
      if (lens) {
        await client.invalidateQueries({ queryKey: [...reportingLeLensQueryKeys.detail(id), 'data'] });
        await client.refetchQueries({ exact: true, queryKey: reportingLeLensQueryKeys.detail(id) });
        await refreshCertifiedShelves(client);
      }
    },
    retry: false,
  });
}

function lensDataDefinition(lens: Lens) {
  return JSON.stringify({
    datasets: lens.spec.datasets,
    filters: lens.spec.filters,
    cards: lens.spec.cards.map(({ id, datasetId, view }) => ({ id, datasetId, view })),
  });
}

export function useLensLiveState(lens: Lens | undefined, editing: boolean, canEdit: boolean) {
  const client = useQueryClient();
  const query = useQuery({
    queryKey: ['lens-live-state', lens?.id],
    enabled: Boolean(lens) && canEdit,
    refetchInterval: canEdit ? 5000 : false,
    refetchIntervalInBackground: false,
    retry: false,
    queryFn: async () =>
      (await request(resolveReportingLeLensUrl(`/api/reporting/le-lenses/${lens.id}`))) as LensResponse,
  });
  const remote = query.data;
  useEffect(() => {
    if (!lens || !remote || editing || remote.lens.version <= lens.version) {
      return;
    }
    client.setQueryData(reportingLeLensQueryKeys.detail(lens.id), remote);
    if (lensDataDefinition(lens) !== lensDataDefinition(remote.lens)) {
      client.invalidateQueries({ queryKey: [...reportingLeLensQueryKeys.detail(lens.id), 'data'] });
    }
  }, [client, editing, lens, remote]);
  const status = (query.error as { status?: number } | null)?.status;
  return {
    hasRemoteChanges: Boolean(lens && remote && remote.lens.version > lens.version),
    accessError: status && [401, 403, 404].includes(status) ? query.error : null,
  };
}
